import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { CheckCircle2, Circle, DollarSign, ShieldCheck, Landmark, Loader2, AlertTriangle, RefreshCcw } from "lucide-react";

const STEPS = [
  { id: "broker", label: "Connect live broker", icon: Landmark },
  { id: "acknowledge", label: "Acknowledge real money", icon: AlertTriangle },
  { id: "test", label: "Test with $1 order", icon: DollarSign },
  { id: "ramp", label: "Raise daily cap", icon: ShieldCheck },
];

export default function RealMoneyOnboarding() {
  const [step, setStep] = useState(0);
  const [status, setStatus] = useState({ enabled: false });
  const [broker, setBroker] = useState({ connected: false, mode: "" });
  const [cap, setCap] = useState(50);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const refresh = async () => {
    try {
      const [rm, ibkr] = await Promise.all([
        api.get("/real-money/status"),
        api.get("/broker/ibkr/status").catch(() => ({ data: {} })),
      ]);
      setStatus(rm.data);
      setBroker(ibkr.data || {});
      // Auto-advance
      if (ibkr.data?.connected && ibkr.data?.mode === "live") setStep((s) => Math.max(s, 1));
      if (rm.data?.enabled) setStep((s) => Math.max(s, 2));
    } catch { /* silent */ }
  };
  useEffect(() => { refresh(); const iv = setInterval(refresh, 5000); return () => clearInterval(iv); }, []);

  const doAcknowledge = async () => {
    setBusy(true);
    try {
      await api.post("/real-money/enable", { acknowledged: true, max_daily_notional_usd: 50 });
      toast.success("Real-money mode enabled with a $50 safety cap");
      await refresh();
      setStep(2);
    } catch (e) { toast.error(e?.response?.data?.detail || "Enable failed"); }
    finally { setBusy(false); }
  };

  const doTestOrder = async () => {
    setBusy(true);
    try {
      await api.post("/orders", {
        symbol: "AAPL", asset_type: "stock", side: "buy", quantity: 1, route: "ibkr_live",
      });
      toast.success("Test order fired via live broker");
      setStep(3);
    } catch (e) { toast.error(e?.response?.data?.detail || "Test order failed"); }
    finally { setBusy(false); }
  };

  const doRaiseCap = async () => {
    setBusy(true);
    try {
      await api.post("/real-money/enable", { acknowledged: true, max_daily_notional_usd: cap });
      toast.success(`Daily cap raised to $${cap}`);
      await refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const doReset = async () => {
    if (!window.confirm("Reset onboarding? This clears your real-money flag + daily cap. Live broker link is untouched.")) return;
    setBusy(true);
    try {
      await api.post("/real-money/reset");
      toast.success("Onboarding reset — starting fresh");
      setStep(0);
      setCap(50);
      await refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Reset failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="rm-onboarding-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Guided</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold mt-1 flex items-center gap-2">
            <DollarSign className="w-7 h-7" /> Go Live
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            A safe, four-step ramp from paper trading to real-money bots. Each step gates the next.
          </p>
        </div>
        <Button
          onClick={doReset}
          disabled={busy}
          variant="ghost"
          size="sm"
          data-testid="rm-reset-btn"
          className="rounded-full text-muted-foreground hover:text-foreground"
          title="Reset onboarding — start over"
        >
          <RefreshCcw className="w-3.5 h-3.5 mr-1.5" /> Start over
        </Button>
      </div>

      {/* Stepper */}
      <div className="flex items-center gap-1 flex-wrap" data-testid="rm-stepper">
        {STEPS.map((s, i) => {
          const done = step > i;
          const active = step === i;
          const Ico = s.icon;
          return (
            <div key={s.id} className={`flex items-center gap-2 px-3 py-2 rounded-full text-xs ${
              done ? "bg-[hsl(var(--gain))]/10 text-[hsl(var(--gain))]" :
              active ? "bg-foreground text-background" :
              "text-muted-foreground"
            }`} data-testid={`rm-step-${s.id}`}>
              {done ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Circle className="w-3.5 h-3.5" />}
              <Ico className="w-3.5 h-3.5" />
              {s.label}
            </div>
          );
        })}
      </div>

      {/* Step 1 */}
      <Card className="p-5 rounded-2xl" data-testid="rm-step-1-card">
        <div className="text-lg font-display font-semibold mb-1 flex items-center gap-2">
          <Landmark className="w-4 h-4" /> 1. Connect live broker
        </div>
        <div className="text-xs text-muted-foreground mb-3">
          Aanar needs a live broker in place before it can route real orders. Currently supported: Interactive Brokers Client Portal (live mode), Alpaca live, Tradier live.
        </div>
        <div className="flex items-center justify-between">
          <div className="text-sm">
            IBKR: <span className={broker.connected && broker.mode === "live" ? "gain" : "text-muted-foreground"}>
              {broker.connected ? `${broker.mode} · connected` : "not connected"}
            </span>
          </div>
          <Button variant="outline" onClick={() => navigate("/ibkr")} data-testid="rm-goto-ibkr" className="rounded-full">
            Manage brokers
          </Button>
        </div>
      </Card>

      {/* Step 2 */}
      <Card className={`p-5 rounded-2xl ${step === 1 ? "border-2 border-foreground" : ""}`} data-testid="rm-step-2-card">
        <div className="text-lg font-display font-semibold mb-1 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-[hsl(var(--loss))]" /> 2. Acknowledge real money
        </div>
        <div className="text-xs text-muted-foreground mb-3">
          Explicit acknowledgment that losses will be real. We start with a $50 daily cap — you'll raise it in the final step once you're comfortable.
        </div>
        <Button onClick={doAcknowledge} disabled={busy || status.enabled || !(broker.connected && broker.mode === "live")}
                data-testid="rm-acknowledge-btn" className="rounded-full">
          {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
          {status.enabled ? "Already enabled ✓" : "I understand — enable with $50 cap"}
        </Button>
      </Card>

      {/* Step 3 */}
      <Card className={`p-5 rounded-2xl ${step === 2 ? "border-2 border-foreground" : ""}`} data-testid="rm-step-3-card">
        <div className="text-lg font-display font-semibold mb-1 flex items-center gap-2">
          <DollarSign className="w-4 h-4" /> 3. Fire a $1 test order
        </div>
        <div className="text-xs text-muted-foreground mb-3">
          Places a 1-share AAPL buy through your live broker. If it fills, the pipe is working — you can safely automate.
        </div>
        <Button onClick={doTestOrder} disabled={busy || !status.enabled || step < 2}
                data-testid="rm-test-btn" className="rounded-full">
          {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
          Fire test order
        </Button>
      </Card>

      {/* Step 4 */}
      <Card className={`p-5 rounded-2xl ${step === 3 ? "border-2 border-foreground" : ""}`} data-testid="rm-step-4-card">
        <div className="text-lg font-display font-semibold mb-1 flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-[hsl(var(--gain))]" /> 4. Raise your daily cap
        </div>
        <div className="text-xs text-muted-foreground mb-3">
          Set a comfortable daily notional cap. Aanar rejects any real-money order that would push today's total past this.
        </div>
        <div className="flex items-center gap-2">
          <Input type="number" value={cap} min={50} step={50}
                 onChange={(e) => setCap(parseInt(e.target.value || "0"))}
                 data-testid="rm-cap-input" className="max-w-xs rounded-lg" />
          <Button onClick={doRaiseCap} disabled={busy || step < 3}
                  data-testid="rm-raise-cap-btn" className="rounded-full">Save cap</Button>
        </div>
        {status.max_daily_notional_usd && (
          <div className="text-xs text-muted-foreground mt-2">
            Current cap: <span className="font-mono-tab">${status.max_daily_notional_usd}</span>
          </div>
        )}
      </Card>
    </div>
  );
}
